import { t } from "../i18n";
import { LANDING_WRAP, LandingShell, ProofList } from "../ui/LandingShell";
import { WallGlyph } from "../ui/Wordmark";
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { LangSwitch } from "../i18n/LangSwitch";

/**
 * Oturum sayfaları. Varsayılan (README §4.1): tam ekran #101215 + 20 px ızgara, ortada 360 px açılış penceresi.
 * `variant="landing"` (yalnız /giris; design_handoff_giris_sayfasi): başlık çubuğu → solda değer önerisi + sağda `children` (giriş kartı) → yasal footer.
 * Alanlar gövde fontunda (mono değil); erişilebilir adlar aynı (section aria-label = başlık, h1).
 */
export function AuthLayout({ title, children, footer, variant }: { title: string; children: ReactNode; footer?: ReactNode; variant?: "landing" }): JSX.Element {
  if (variant === "landing") return <Landing>{children}</Landing>;
  return (
    <main className="cad-auth min-h-screen grid place-items-center p-6 bg-[#101215] bg-[linear-gradient(#1B1E22_1px,transparent_1px),linear-gradient(90deg,#1B1E22_1px,transparent_1px)] bg-[size:20px_20px] text-[12px]">
      <div className="w-full max-w-[360px] grid gap-1.5">
        <section className="bg-cad-panel border border-cad-strong shadow-[0_18px_40px_-14px_rgb(0_0_0/0.6)] flex flex-col" aria-label={title}>
          <div className="h-[26px] flex items-center gap-1.5 px-2 bg-cad-head border-b border-cad-line text-[11px] font-semibold text-cad-ink2">
            <span className="inline-flex items-center gap-1 text-cad-accent"><WallGlyph size={16} /> {t("TaşDuvar")}</span>
            <span className="text-cad-ink3">·</span>
            <h1 className="text-[11px] font-semibold m-0">{title}</h1>
          </div>
          <div className="p-3 grid gap-2.5">{children}</div>
          {footer ? <div className="flex flex-wrap gap-x-3 items-center px-3 py-2 bg-cad-sub border-t border-cad-line text-[11px] text-cad-ink2">{footer}</div> : null}
        </section>
        <div className="flex items-center justify-end font-mono text-[10px] text-cad-offink"><LangSwitch /></div>
      </div>
    </main>
  );
}

const PROOF: [string, string, string][] = [
  ["01", "Anlık kontrol", "Her girdi değişiminde güvenlik sayıları yeniden hesaplanır."],
  ["02", "Teslim edilebilir çıktı", "DXF kesit, DOCX hesap raporu, XLSX metraj — ÇŞB birim fiyatlarıyla."],
  ["03", "Yönetmeliğe bağlı", "TS 7994, Eurocode 7 (EN 1997-1) ve TBDY 2018'e göre."],
];

function Landing({ children }: { children: ReactNode }): JSX.Element {
  return (
    <LandingShell>
      <main className={`${LANDING_WRAP} flex-1 grid content-center items-center gap-[clamp(40px,6vw,80px)] py-[clamp(32px,6vw,72px)] grid-cols-[repeat(auto-fit,minmax(min(100%,380px),1fr))]`}>
        <section className="max-w-[540px]">
          <h1 className="text-[clamp(30px,3.6vw,42px)] leading-[1.14] font-semibold tracking-[-0.03em]">{t("İstinat duvarı projesini bir öğleden sonrada bitirin.")}</h1>
          <p className="mt-[18px] text-[16px] leading-[1.6] text-[#3A3F45]">{t("Geometri, zemin ve yük girdilerinden devrilme, kayma, taban gerilmesi ve toptan göçme kontrollerini; kesit çizimini; hesap raporunu ve metrajı tek dosyadan üretin.")}</p>
          <ProofList className="mt-[32px]" items={PROOF} />
        </section>
        {children}
      </main>
    </LandingShell>
  );
}

/**
 * Parola alanı: göster/gizle metin düğmesi (aria-pressed; erişilebilir adı "Göster"/"Gizle" — "Parola…" ile başlasaydı
 * e2e `getByLabel("Parola")` iki öğe bulurdu) + Caps Lock uyarısı. `aside` etiket satırının sağına (ör. "Parolamı unuttum").
 */
export function PasswordField({ id, label, value, onChange, autoComplete, aside, error, invalid, inputRef }: {
  id: string; label: string; value: string; onChange: (v: string) => void; autoComplete: string;
  aside?: ReactNode; error?: string; invalid?: boolean; inputRef?: (el: HTMLInputElement | null) => void;
}): JSX.Element {
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => setCaps(e.getModifierState("CapsLock"));
  const describedBy = [error ? `${id}-err` : "", caps ? `${id}-caps` : ""].filter(Boolean).join(" ") || undefined;
  return (
    <div className="pw-field flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      <div className="pw-box" data-invalid={error || invalid ? "true" : undefined}>
        <input id={id} ref={inputRef} type={show ? "text" : "password"} value={value} autoComplete={autoComplete}
          aria-invalid={error || invalid ? "true" : undefined} aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.value)} onKeyDown={onKey} onKeyUp={onKey} onBlur={() => setCaps(false)} />
        <button type="button" aria-pressed={show} aria-controls={id} onClick={() => setShow(!show)}>{show ? t("Gizle") : t("Göster")}</button>
      </div>
      {error ? <p id={`${id}-err`} className="field-err">{error}</p> : null}
      {caps ? <p id={`${id}-caps`} role="status" className="caps-warn">{t("Caps Lock açık.")}</p> : null}
    </div>
  );
}
